import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DuplicateWarningDialog from '../components/common/DuplicateWarningDialog';
import { trackAction, trackModalOpen, trackSearch } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MapPin, Plus, Edit, Trash2, MoreVertical, Loader2, RotateCcw, Archive, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { formatShortDate } from '../lib/utils';

const LocationsPage = () => {
  const { api, isAdmin, hasPermission, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [deletedLocations, setDeletedLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('active');
  
  // Permission check for bulk delete
  const canBulkDelete = hasPermission('bulk_delete_locations') || isSuperAdmin();
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const [activeRes, deletedRes] = await Promise.all([
        api().get('/locations'),
        isAdmin ? api().get('/locations/deleted').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setLocations(activeRes.data);
      setDeletedLocations(deletedRes.data || []);
    } catch (error) {
      toast.error('Error al cargar ubicaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (locationId, locationName) => {
    try {
      await api().post(`/locations/${locationId}/restore`);
      toast.success(`Ubicación "${locationName}" restaurada`);
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  // Bulk selection functions
  const toggleSelectItem = (itemId, e) => {
    e?.stopPropagation?.();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const currentList = activeTab === 'active' ? locations : deletedLocations;
    if (selectedItems.size === currentList.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(currentList.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(`¿Eliminar ${selectedItems.size} ubicación(es)?`)) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = [];
    
    try {
      for (const itemId of selectedItems) {
        try {
          await api().delete(`/locations/${itemId}`);
          deleted++;
        } catch (error) {
          const errorMsg = error.response?.data?.detail || 'Error desconocido';
          const location = locations.find(l => l.id === itemId);
          errors.push(`${location?.name || itemId}: ${errorMsg}`);
          console.error(`Error deleting location ${itemId}:`, error);
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} ubicación(es) eliminada(s)`);
      }
      
      if (errors.length > 0) {
        toast.error(`No se pudieron eliminar ${errors.length} ubicación(es):\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}`, {
          duration: 8000
        });
      }
      
      setSelectedItems(new Set());
      loadLocations();
    } catch (error) {
      toast.error('Error al eliminar ubicaciones');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateModal = () => {
    setEditingLocation(null);
    setForm({ name: '', description: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setForm({ name: location.name, description: location.description || '' });
    setIsModalOpen(true);
  };

  const [duplicateError, setDuplicateError] = useState(null);

  const performCreateLoc = async (forceCreate = false) => {
    const url = forceCreate ? '/locations?force_create=true' : '/locations';
    return api().post(url, form);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingLocation) {
        await api().put(`/locations/${editingLocation.id}`, form);
        toast.success('Ubicación actualizada');
      } else {
        await performCreateLoc(false);
        toast.success('Ubicación creada');
      }
      setIsModalOpen(false);
      loadLocations();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate) {
        setDuplicateError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = async () => {
    setDuplicateError(null);
    setSaving(true);
    try {
      await performCreateLoc(true);
      toast.success('Ubicación creada (duplicado forzado)');
      setIsModalOpen(false);
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta ubicación?')) return;
    
    try {
      await api().delete(`/locations/${id}`);
      toast.success('Ubicación eliminada');
      loadLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ubicaciones</h1>
          <p className="text-muted-foreground mt-1">
            Administra las ubicaciones físicas
          </p>
        </div>
        <Button onClick={openCreateModal} className="btn-primary" data-testid="add-location-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Ubicación
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active">
            Activas
            <Badge variant="secondary" className="ml-2">{locations.length}</Badge>
          </TabsTrigger>
          {canBulkDelete && (
            <TabsTrigger value="deleted" className="flex items-center gap-1">
              <Archive className="h-4 w-4" />
              Eliminadas
              {deletedLocations.length > 0 && (
                <Badge variant="destructive" className="ml-1">{deletedLocations.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active">
          {/* Bulk Selection Bar */}
          {canBulkDelete && locations.length > 0 && (
            <div className="flex items-center gap-4 p-3 mb-4 bg-muted/50 rounded-lg border">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              >
                {selectedItems.size === locations.length && locations.length > 0 ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                {selectedItems.size === locations.length ? 'Deseleccionar' : 'Seleccionar todos'}
              </button>
              
              {selectedItems.size > 0 && (
                <>
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.size} seleccionada(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Eliminar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems(new Set())}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}
          
          <Card className="card-hover">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    {canBulkDelete && <TableHead className="w-12"></TableHead>}
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Fecha de Creación</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={canBulkDelete ? 5 : 4}>
                          <Skeleton className="h-12 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : locations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canBulkDelete ? 5 : 4} className="text-center py-12">
                        <MapPin className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">No hay ubicaciones</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    locations.map((loc) => (
                      <TableRow key={loc.id} className="table-row" data-testid={`location-row-${loc.id}`}>
                        {canBulkDelete && (
                          <TableCell>
                            <button
                              onClick={(e) => toggleSelectItem(loc.id, e)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                            >
                              {selectedItems.has(loc.id) ? (
                                <CheckSquare className="h-5 w-5 text-primary" />
                              ) : (
                                <Square className="h-5 w-5 text-muted-foreground" />
                              )}
                            </button>
                          </TableCell>
                        )}
                        <TableCell className="font-medium">{loc.name}</TableCell>
                        <TableCell className="text-muted-foreground">{loc.description || '-'}</TableCell>
                        <TableCell>{formatShortDate(loc.created_at)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(loc)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(loc.id)} className="text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {canBulkDelete && (
          <TabsContent value="deleted">
            <Card className="card-hover border-red-500/20">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Nombre</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Eliminada</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedLocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-12">
                          <Archive className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                          <p className="text-muted-foreground">No hay ubicaciones eliminadas</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      deletedLocations.map((loc) => (
                        <TableRow key={loc.id} className="table-row bg-red-500/5">
                          <TableCell className="font-medium">{loc.name}</TableCell>
                          <TableCell className="text-muted-foreground">{loc.description || '-'}</TableCell>
                          <TableCell>{formatShortDate(loc.deleted_at)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleRestore(loc.id, loc.name)}
                                className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restaurar
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar "${loc.name}" PERMANENTEMENTE?\n\nEsta acción NO se puede deshacer.`)) return;
                                  try {
                                    await api().delete(`/locations/${loc.id}/permanent`);
                                    toast.success(`"${loc.name}" eliminado permanentemente`);
                                    loadLocations();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Error al eliminar permanentemente');
                                  }
                                }}
                                title="Eliminar permanentemente"
                                data-testid={`location-permanent-delete-${loc.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Editar Ubicación' : 'Nueva Ubicación'}</DialogTitle>
            <DialogDescription>
              {editingLocation ? 'Modifica los datos de la ubicación' : 'Crea una nueva ubicación física'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre de la ubicación"
                data-testid="location-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descripción de la ubicación..."
                rows={3}
                data-testid="location-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-location-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingLocation ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateWarningDialog
        duplicateError={duplicateError}
        onCancel={() => setDuplicateError(null)}
        onForceCreate={handleForceCreate}
        entityLabel="ubicación"
      />
    </div>
  );
};

export default LocationsPage;
